/**
 * Icon Component
 * Emoji-based icon system (no icon webfont)
 *
 * Usage:
 *   <Icon name="settings" />
 *   <Icon name="play_arrow" size={24} />
 *   <Icon name="school" className="text-primary" />
 */

import React from 'react';

// Icon name mapping (kept stable for callers)
const ICON_EMOJI_MAP = {
  // Navigation & General
  home: '🏠',
  settings: '⚙️',
  close: '✖️',
  menu: '☰',
  arrow_back: '⬅️',
  check: '✅',
  add: '➕',
  edit: '✏️',
  delete: '🗑️',
  more_vert: '⋮',

  // Missing icons added for compatibility
  crown: '👑',
  accuracy: '🎯',
  trophy: '🏆',
  gamepad: '🎮',
  star: '⭐',
  streak: '🔥',
  target: '🎯',
  school: '🏫',
  fire: '🔥',
  play: '▶️',

  // Dashboard & Stats
  dashboard: '📊',
  analytics: '📈',
  leaderboard: '🏆',
  stats: '📊',
  barChart: '📊',
  insights: '💡',
  history: '📅',
  calendar: '📅',
  today: '📅',
  dateRange: '📆',
  event: '🗓️',

  // Users & Profiles
  person: '👤',
  people: '👥',
  family: '👨‍👩‍👧‍👦',
  child: '👶',
  childCare: '👶',
  admin: '🛠️',

  // Security & Auth
  lock: '🔒',
  unlock: '🔓',
  key: '🔑',
  security: '🔐',
  visibility: '👁️',
  visibilityOff: '🙈',
  fingerprint: '👆',

  // Actions & States
  logout: '🚪',
  login: '🚀',
  save: '💾',
  download: '📥',
  upload: '📤',
  share: '📤',
  send: '📤',
  refresh: '🔄',
  sync: '🔄',
  inbox: '📭',

  // Feedback & Communication
  feedback: '💬',
  email: '✉️',
  message: '💬',
  notification: '🔔',
  help: '❓',
  info: 'ℹ️',
  warning: '⚠️',
  error: '❌',
  success: '✅',

  // Media & Sound
  volumeUp: '🔊',
  volumeOff: '🔇',
  music: '🎵',

  // Math & Learning
  calculate: '🧮',
  functions: '🧮',
  quiz: '❓',
  book: '📖',
  lightbulb: '💡',
  science: '🔬',
  timer: '⏱️',
  clock: '🕐',
  schedule: '⏰',

  // Math Operations (topic types)
  addition: '➕',
  subtraction: '➖',
  multiplication: '✖️',
  division: '➗',
  factors: '🔢',
  primes: '⭐',
  factorials: '❗',
  randomMix: '🎲',
  sequences: '🔄',
  time: '🕐',
  moneyTopic: '💰',

  // Category icons
  numbers: '🔢',
  fraction: '🥧',
  geometry: '⬡',
  ruler: '📏',
  variable: '𝑥',
  function: 'ƒ',

  // Money
  money: '💰',
  coin: '🪙',

  // Misc
  robot: '🤖',
  rocket: '🚀',
  sparkles: '✨',
  celebration: '🎉',
  plant: '🌱',
  heart: '❤️',
  block: '🚫',
  backspace: '⌫',
  hourglass: '⏳',
  phone: '📱',
  computer: '💻',
  fitness: '🏋️',
  bug: '🐛',

  // Medals & Awards
  medalGold: '🥇',
  medalSilver: '🥈',
  medalBronze: '🥉',
  workspace_premium: '⭐',

  // Arrows & Direction
  arrowUp: '⬆️',
  arrowDown: '⬇️',
  arrowLeft: '⬅️',
  arrowRight: '➡️',
  expandMore: '▼',
  expandLess: '▲',
  unfoldMore: '⇅',
  chevronRight: '›',
  chevronLeft: '‹',
  search: '🔍',
};

export type IconName = keyof typeof ICON_EMOJI_MAP;

interface IconProps {
  /** Icon name from ICON_MAP */
  name: IconName;
  /**
   * Size in pixels (default: 24)
   * M3 standard sizes: 20 (small), 24 (default), 40 (large display), 48 (hero)
   */
  size?: 20 | 24 | 40 | 48;
  /** Use filled variant (default: false - outlined) */
  filled?: boolean;
  /** Additional CSS classes */
  className?: string;
  /** Accessibility label */
  'aria-label'?: string;
  /** Hide from screen readers (default: true for decorative icons) */
  'aria-hidden'?: boolean;
}

/**
 * Icon component (emoji-based; no icon webfont)
 */
export const Icon: React.FC<IconProps> = ({
  name,
  size = 24,
  className = '',
  'aria-label': ariaLabel,
  'aria-hidden': ariaHidden = true,
}) => {
  const glyph = ICON_EMOJI_MAP[name];

  return (
    <span
      className={`select-none inline-flex items-center justify-center leading-none ${className}`}
      style={{
        fontSize: size,
        width: size,
        height: size,
      }}
      aria-label={ariaLabel}
      aria-hidden={ariaLabel ? false : ariaHidden}
      role={ariaLabel ? 'img' : undefined}
    >
      {glyph}
    </span>
  );
};
